import type { CSSProperties, SVGProps } from 'react';

export type IconName =
  // Navigation & Core Modules
  | 'dashboard'
  | 'employees'
  | 'attendance'
  | 'leaves'
  | 'leave'
  | 'payroll'
  | 'finance'
  | 'accounting'
  | 'expenses'
  | 'loans'
  | 'assets'
  | 'recruitment'
  | 'exit'
  | 'departments'
  | 'audit'
  | 'settings'
  | 'database'
  | 'fileSpreadsheet'
  | 'layers'
  | 'menu'
  | 'close'
  | 'search'

  // HR & People
  | 'user'
  | 'users'
  | 'userRound'
  | 'usersRound'
  | 'userPlus'
  | 'userCheck'
  | 'userX'
  | 'userRoundCheck'
  | 'briefcase'
  | 'briefcaseBusiness'
  | 'award'
  | 'cake'
  | 'sparkles'

  // Attendance & Time
  | 'calendar'
  | 'calendarDays'
  | 'calendarCheck'
  | 'clock'

  // Payroll & Tax
  | 'wallet'
  | 'banknote'
  | 'coins'
  | 'creditCard'
  | 'receipt'
  | 'invoice'

  // Finance, Banking & Ledger
  | 'landmark'
  | 'bank'
  | 'calculator'
  | 'bookOpen'
  | 'notebookTabs'
  | 'list'
  | 'arrowDownToLine'
  | 'circleDollarSign'
  | 'arrowUpFromLine'
  | 'cash'

  // Clients, Vendors, Contracts & Projects
  | 'building'
  | 'building2'
  | 'store'
  | 'handshake'
  | 'fileSignature'
  | 'clipboardList'
  | 'fileCheck'
  | 'folderKanban'
  | 'projects'

  // Assets & Hardware
  | 'laptop'
  | 'monitor'
  | 'package'
  | 'boxes'
  | 'packageCheck'
  | 'packageX'
  | 'rotateCcw'
  | 'smartphone'

  // Analytics & Reports
  | 'trendingUp'
  | 'trendingDown'
  | 'chartNoAxesCombined'
  | 'barChart3'
  | 'lineChart'
  | 'pieChart'
  | 'scale'

  // Documents & Storage
  | 'files'
  | 'file'
  | 'fileText'
  | 'filePdf'
  | 'fileArchive'
  | 'folder'

  // Communication & Security
  | 'messageSquare'
  | 'mail'
  | 'phone'
  | 'shield'
  | 'shieldCheck'
  | 'history'
  | 'lock'
  | 'unlock'
  | 'keyRound'
  | 'bell'

  // Actions & Controls
  | 'plus'
  | 'edit'
  | 'pencil'
  | 'trash'
  | 'trash2'
  | 'eye'
  | 'download'
  | 'upload'
  | 'share'
  | 'send'
  | 'check'
  | 'x'
  | 'printer'
  | 'refresh'
  | 'filter'
  | 'listFilter'
  | 'slidersHorizontal'
  | 'chevronRight'
  | 'chevronDown'
  | 'chevronLeft'
  | 'chevronUp'
  | 'moreVertical'
  | 'moreHorizontal'
  | 'arrowRight'
  | 'arrowLeft'
  | 'arrowUpDown'
  | 'externalLink'

  // Status & Alerts
  | 'checkCircle'
  | 'circleCheck'
  | 'alertCircle'
  | 'alertTriangle'
  | 'triangleAlert'
  | 'info'
  | 'infoCircle'
  | 'clockCircle'
  | 'xCircle'
  | 'circleX'
  | 'helpCircle';

export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | number;

const SIZE_MAP: Record<string, number> = {
  xs: 14,
  sm: 16,
  md: 18,
  lg: 20,
  xl: 24,
  '2xl': 32,
  '3xl': 40,
  '4xl': 48,
};

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  size?: IconSize;
  color?: string;
  strokeWidth?: number;
  className?: string;
  title?: string;
  style?: CSSProperties;
}

export function Icon({
  name,
  size = 'md',
  color = 'currentColor',
  strokeWidth = 2,
  className = '',
  title,
  style,
  ...props
}: IconProps) {
  const pixelSize = typeof size === 'number' ? size : SIZE_MAP[size] || 18;

  const svgStyle: CSSProperties = {
    display: 'inline-block',
    verticalAlign: 'middle',
    flexShrink: 0,
    width: pixelSize,
    height: pixelSize,
    ...style,
  };

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={svgStyle}
      className={`svg-icon icon-${name} ${className}`.trim()}
      aria-hidden={!title}
      role={title ? 'img' : 'presentation'}
      {...props}
    >
      {title && <title>{title}</title>}
      {renderIconPath(name)}
    </svg>
  );
}

// ----------------- CENTRAL SEMANTIC MODULE ICON -----------------
export type ModuleType =
  | 'dashboard'
  | 'employees'
  | 'attendance'
  | 'leave'
  | 'leaves'
  | 'payroll'
  | 'finance'
  | 'accounting'
  | 'invoices'
  | 'clients'
  | 'vendors'
  | 'contracts'
  | 'projects'
  | 'assets'
  | 'reports'
  | 'documents'
  | 'recruitment'
  | 'exit'
  | 'departments'
  | 'audit'
  | 'settings'
  | 'expenses'
  | 'loans'
  | 'grievances'
  | 'bulkImport';

const MODULE_ICON_MAP: Record<ModuleType, IconName> = {
  dashboard: 'dashboard',
  employees: 'users',
  attendance: 'calendar',
  leave: 'calendarDays',
  leaves: 'calendarDays',
  payroll: 'wallet',
  finance: 'landmark',
  accounting: 'bookOpen',
  invoices: 'receipt',
  clients: 'building2',
  vendors: 'store',
  contracts: 'fileSignature',
  projects: 'folderKanban',
  assets: 'laptop',
  reports: 'barChart3',
  documents: 'files',
  recruitment: 'userPlus',
  exit: 'exit',
  departments: 'building',
  audit: 'shieldCheck',
  settings: 'settings',
  expenses: 'receipt',
  loans: 'creditCard',
  grievances: 'shield',
  bulkImport: 'upload',
};

export interface ModuleIconProps {
  module: ModuleType;
  size?: IconSize;
  color?: string;
  className?: string;
  title?: string;
  style?: CSSProperties;
}

export function ModuleIcon({ module, size = 'md', color, className, title, style }: ModuleIconProps) {
  const iconName = MODULE_ICON_MAP[module] || 'dashboard';
  return <Icon name={iconName} size={size} color={color} className={className} title={title} style={style} />;
}

// ----------------- CENTRAL SEMANTIC STATUS ICON -----------------
export type StatusType = 'success' | 'warning' | 'danger' | 'error' | 'pending' | 'info' | 'neutral';

const STATUS_ICON_MAP: Record<StatusType, { name: IconName; defaultColor: string }> = {
  success: { name: 'checkCircle', defaultColor: 'var(--accent-emerald, #10b981)' },
  warning: { name: 'triangleAlert', defaultColor: 'var(--accent-amber, #f59e0b)' },
  danger: { name: 'circleX', defaultColor: 'var(--accent-ruby, #ef4444)' },
  error: { name: 'circleX', defaultColor: 'var(--accent-ruby, #ef4444)' },
  pending: { name: 'clock', defaultColor: 'var(--accent-amber, #f59e0b)' },
  info: { name: 'info', defaultColor: 'var(--accent-cyan, #06b6d4)' },
  neutral: { name: 'infoCircle', defaultColor: 'var(--ink-muted, #94a3b8)' },
};

export interface StatusIconProps {
  status: StatusType;
  size?: IconSize;
  color?: string;
  className?: string;
  title?: string;
  style?: CSSProperties;
}

export function StatusIcon({ status, size = 'md', color, className, title, style }: StatusIconProps) {
  const cfg = STATUS_ICON_MAP[status] || STATUS_ICON_MAP.info;
  return (
    <Icon
      name={cfg.name}
      size={size}
      color={color || cfg.defaultColor}
      className={className}
      title={title}
      style={style}
    />
  );
}

// ----------------- SVG PATH RENDERER -----------------
function renderIconPath(name: IconName) {
  switch (name) {
    // Navigation & Core
    case 'dashboard':
      return (
        <>
          <rect x="3" y="3" width="7" height="9" rx="1.5" />
          <rect x="14" y="3" width="7" height="5" rx="1.5" />
          <rect x="14" y="12" width="7" height="9" rx="1.5" />
          <rect x="3" y="16" width="7" height="5" rx="1.5" />
        </>
      );

    case 'employees':
    case 'users':
    case 'usersRound':
      return (
        <>
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </>
      );

    case 'user':
    case 'userRound':
      return (
        <>
          <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </>
      );

    case 'userPlus':
      return (
        <>
          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="8.5" cy="7" r="4" />
          <line x1="20" y1="8" x2="20" y2="14" />
          <line x1="23" y1="11" x2="17" y2="11" />
        </>
      );

    case 'userCheck':
    case 'userRoundCheck':
      return (
        <>
          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="8.5" cy="7" r="4" />
          <polyline points="17 11 19 13 23 9" />
        </>
      );

    case 'userX':
      return (
        <>
          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="8.5" cy="7" r="4" />
          <line x1="18" y1="8" x2="22" y2="12" />
          <line x1="22" y1="8" x2="18" y2="12" />
        </>
      );

    case 'attendance':
    case 'clock':
      return (
        <>
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </>
      );

    case 'calendar':
      return (
        <>
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </>
      );

    case 'calendarDays':
    case 'leaves':
    case 'leave':
      return (
        <>
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
          <path d="M8 14h.01" />
          <path d="M12 14h.01" />
          <path d="M16 14h.01" />
          <path d="M8 18h.01" />
          <path d="M12 18h.01" />
          <path d="M16 18h.01" />
        </>
      );

    case 'calendarCheck':
      return (
        <>
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
          <polyline points="9 16 11 18 15 14" />
        </>
      );

    case 'payroll':
    case 'wallet':
      return (
        <>
          <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
          <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
          <path d="M18 12a2 2 0 0 0 0 4h4v-4z" />
        </>
      );

    case 'banknote':
    case 'cash':
      return (
        <>
          <rect x="2" y="6" width="20" height="12" rx="2" />
          <circle cx="12" cy="12" r="2" />
          <path d="M6 12h.01M18 12h.01" />
        </>
      );

    case 'coins':
    case 'circleDollarSign':
      return (
        <>
          <circle cx="12" cy="12" r="10" />
          <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
          <path d="M12 18V6" />
        </>
      );

    case 'finance':
    case 'accounting':
    case 'landmark':
    case 'bank':
      return (
        <>
          <line x1="3" y1="21" x2="21" y2="21" />
          <line x1="3" y1="10" x2="21" y2="10" />
          <polyline points="12 3 2 10 22 10 12 3" />
          <line x1="5" y1="10" x2="5" y2="21" />
          <line x1="9" y1="10" x2="9" y2="21" />
          <line x1="15" y1="10" x2="15" y2="21" />
          <line x1="19" y1="10" x2="19" y2="21" />
        </>
      );

    case 'expenses':
    case 'receipt':
    case 'invoice':
      return (
        <>
          <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1Z" />
          <line x1="8" y1="7" x2="16" y2="7" />
          <line x1="8" y1="11" x2="16" y2="11" />
          <line x1="8" y1="15" x2="12" y2="15" />
        </>
      );

    case 'loans':
    case 'creditCard':
      return (
        <>
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <line x1="2" y1="10" x2="22" y2="10" />
        </>
      );

    case 'calculator':
      return (
        <>
          <rect x="4" y="2" width="16" height="20" rx="2" />
          <line x1="8" y1="6" x2="16" y2="6" />
          <line x1="16" y1="14" x2="16" y2="18" />
          <path d="M16 10h.01" />
          <path d="M12 10h.01" />
          <path d="M8 10h.01" />
          <path d="M12 14h.01" />
          <path d="M8 14h.01" />
          <path d="M12 18h.01" />
          <path d="M8 18h.01" />
        </>
      );

    case 'bookOpen':
      return (
        <>
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
        </>
      );

    case 'notebookTabs':
      return (
        <>
          <rect x="4" y="2" width="16" height="20" rx="2" />
          <line x1="4" y1="6" x2="20" y2="6" />
          <line x1="4" y1="10" x2="20" y2="10" />
          <line x1="4" y1="14" x2="20" y2="14" />
          <line x1="2" y1="8" x2="4" y2="8" />
          <line x1="2" y1="12" x2="4" y2="12" />
          <line x1="2" y1="16" x2="4" y2="16" />
        </>
      );

    case 'arrowDownToLine':
      return (
        <>
          <path d="M12 17V3" />
          <path d="m6 11 6 6 6-6" />
          <path d="M19 21H5" />
        </>
      );

    case 'arrowUpFromLine':
      return (
        <>
          <path d="m18 9-6-6-6 6" />
          <path d="M12 3v14" />
          <path d="M5 21h14" />
        </>
      );

    case 'building':
    case 'departments':
      return (
        <>
          <rect x="4" y="2" width="16" height="20" rx="2" />
          <line x1="9" y1="22" x2="9" y2="18" />
          <line x1="15" y1="22" x2="15" y2="18" />
          <line x1="8" y1="6" x2="10" y2="6" />
          <line x1="14" y1="6" x2="16" y2="6" />
          <line x1="8" y1="10" x2="10" y2="10" />
          <line x1="14" y1="10" x2="16" y2="10" />
          <line x1="8" y1="14" x2="10" y2="14" />
          <line x1="14" y1="14" x2="16" y2="14" />
        </>
      );

    case 'building2':
      return (
        <>
          <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
          <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
          <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
          <path d="M10 6h4" />
          <path d="M10 10h4" />
          <path d="M10 14h4" />
          <path d="M10 18h4" />
        </>
      );

    case 'store':
      return (
        <>
          <path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7" />
          <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
          <path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4" />
          <path d="M2 7h20" />
          <path d="M22 7a2 2 0 0 1-2 2 2 2 0 0 1-2-2 2 2 0 0 1-2 2 2 2 0 0 1-2-2 2 2 0 0 1-2 2 2 2 0 0 1-2-2 2 2 0 0 1-2 2 2 2 0 0 1-2-2" />
        </>
      );

    case 'handshake':
      return (
        <>
          <path d="m11 17 2 2a1 1 0 0 0 1.4 0l6.6-6.6a2 2 0 0 0 0-2.8l-1.4-1.4a2 2 0 0 0-2.8 0L14 11" />
          <path d="m13 7-2-2a1 1 0 0 0-1.4 0L3 11.6a2 2 0 0 0 0 2.8l1.4 1.4a2 2 0 0 0 2.8 0L10 13" />
        </>
      );

    case 'fileSignature':
      return (
        <>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <path d="M18.4 16.6a2.121 2.121 0 0 1-3 3L11 21l-.5-2.5 4.4-4.4a2.121 2.121 0 0 1 3.5 2.5z" />
        </>
      );

    case 'clipboardList':
      return (
        <>
          <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
          <path d="M12 11h4" />
          <path d="M12 16h4" />
          <path d="M8 11h.01" />
          <path d="M8 16h.01" />
        </>
      );

    case 'folderKanban':
    case 'projects':
      return (
        <>
          <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z" />
          <path d="M8 10v4" />
          <path d="M12 10v2" />
          <path d="M16 10v6" />
        </>
      );

    case 'assets':
    case 'laptop':
      return (
        <>
          <path d="M20 16V7a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v9m16 0H4m16 0 1.28 2.55A1 1 0 0 1 20.38 20H3.62a1 1 0 0 1-.9-1.45L4 16" />
        </>
      );

    case 'package':
    case 'boxes':
      return (
        <>
          <path d="m7.5 4.27 9 5.15" />
          <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
          <path d="m3.3 7 8.7 5 8.7-5" />
          <path d="M12 22V12" />
        </>
      );

    case 'packageCheck':
      return (
        <>
          <path d="m16 16 2 2 4-4" />
          <path d="M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14" />
          <path d="m7.5 4.27 9 5.15" />
          <path d="M3.29 7 12 12l8.71-5" />
          <path d="M12 22V12" />
        </>
      );

    case 'packageX':
      return (
        <>
          <path d="m21 16-5 5" />
          <path d="m16 16 5 5" />
          <path d="M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14" />
          <path d="m7.5 4.27 9 5.15" />
          <path d="M3.29 7 12 12l8.71-5" />
          <path d="M12 22V12" />
        </>
      );

    case 'rotateCcw':
      return (
        <>
          <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
          <path d="M3 3v5h5" />
        </>
      );

    case 'recruitment':
    case 'briefcase':
    case 'briefcaseBusiness':
      return (
        <>
          <rect x="2" y="7" width="20" height="14" rx="2" />
          <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
        </>
      );

    case 'exit':
      return (
        <>
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <polyline points="16 17 21 12 16 7" />
          <line x1="21" y1="12" x2="9" y2="12" />
        </>
      );

    case 'audit':
    case 'shieldCheck':
      return (
        <>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <polyline points="9 12 11 14 15 10" />
        </>
      );

    case 'shield':
      return <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />;

    case 'history':
      return (
        <>
          <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
          <path d="M3 3v5h5" />
          <path d="M12 7v5l4 2" />
        </>
      );

    case 'settings':
      return (
        <>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </>
      );

    case 'menu':
      return (
        <>
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </>
      );

    case 'close':
    case 'x':
      return (
        <>
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </>
      );

    case 'search':
      return (
        <>
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </>
      );

    case 'filter':
    case 'listFilter':
      return <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />;

    case 'slidersHorizontal':
      return (
        <>
          <line x1="21" y1="4" x2="14" y2="4" />
          <line x1="10" y1="4" x2="3" y2="4" />
          <line x1="21" y1="12" x2="12" y2="12" />
          <line x1="8" y1="12" x2="3" y2="12" />
          <line x1="21" y1="20" x2="16" y2="20" />
          <line x1="12" y1="20" x2="3" y2="20" />
          <line x1="14" y1="2" x2="14" y2="6" />
          <line x1="8" y1="10" x2="8" y2="14" />
          <line x1="16" y1="18" x2="16" y2="22" />
        </>
      );

    case 'plus':
      return (
        <>
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </>
      );

    case 'edit':
    case 'pencil':
      return (
        <>
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </>
      );

    case 'trash':
    case 'trash2':
      return (
        <>
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          <line x1="10" y1="11" x2="10" y2="17" />
          <line x1="14" y1="11" x2="14" y2="17" />
        </>
      );

    case 'eye':
      return (
        <>
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </>
      );

    case 'download':
      return (
        <>
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </>
      );

    case 'upload':
      return (
        <>
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </>
      );

    case 'share':
      return (
        <>
          <circle cx="18" cy="5" r="3" />
          <circle cx="6" cy="12" r="3" />
          <circle cx="18" cy="19" r="3" />
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
        </>
      );

    case 'send':
    case 'mail':
      return (
        <>
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
        </>
      );

    case 'phone':
      return (
        <>
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
        </>
      );

    case 'messageSquare':
      return <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />;

    case 'check':
      return <polyline points="20 6 9 17 4 12" />;

    case 'printer':
      return (
        <>
          <polyline points="6 9 6 2 18 2 18 9" />
          <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
          <rect x="6" y="14" width="12" height="8" />
        </>
      );

    case 'refresh':
      return (
        <>
          <polyline points="23 4 23 10 17 10" />
          <polyline points="1 20 1 14 7 14" />
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
        </>
      );

    case 'chevronRight':
      return <polyline points="9 18 15 12 9 6" />;
    case 'chevronDown':
      return <polyline points="6 9 12 15 18 9" />;
    case 'chevronLeft':
      return <polyline points="15 18 9 12 15 6" />;
    case 'chevronUp':
      return <polyline points="18 15 12 9 6 15" />;

    case 'moreVertical':
      return (
        <>
          <circle cx="12" cy="12" r="1.5" />
          <circle cx="12" cy="5" r="1.5" />
          <circle cx="12" cy="19" r="1.5" />
        </>
      );

    case 'moreHorizontal':
      return (
        <>
          <circle cx="12" cy="12" r="1.5" />
          <circle cx="19" cy="12" r="1.5" />
          <circle cx="5" cy="12" r="1.5" />
        </>
      );

    case 'lock':
      return (
        <>
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </>
      );

    case 'unlock':
      return (
        <>
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0 1 9.9-1" />
        </>
      );

    case 'keyRound':
      return (
        <>
          <path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z" />
          <circle cx="16.5" cy="7.5" r=".5" fill="currentColor" />
        </>
      );

    case 'bell':
      return (
        <>
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </>
      );

    case 'arrowRight':
      return (
        <>
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </>
      );

    case 'arrowLeft':
      return (
        <>
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </>
      );

    case 'arrowUpDown':
      return (
        <>
          <path d="m7 15 5 5 5-5" />
          <path d="m7 9 5-5 5 5" />
        </>
      );

    case 'externalLink':
      return (
        <>
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
          <polyline points="15 3 21 3 21 9" />
          <line x1="10" y1="14" x2="21" y2="3" />
        </>
      );

    // Status Icons
    case 'checkCircle':
    case 'circleCheck':
      return (
        <>
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </>
      );

    case 'alertTriangle':
    case 'triangleAlert':
      return (
        <>
          <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </>
      );

    case 'alertCircle':
      return (
        <>
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </>
      );

    case 'info':
    case 'infoCircle':
      return (
        <>
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </>
      );

    case 'clockCircle':
      return (
        <>
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 14 14" />
        </>
      );

    case 'xCircle':
    case 'circleX':
      return (
        <>
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </>
      );

    case 'helpCircle':
      return (
        <>
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </>
      );

    case 'award':
      return (
        <>
          <circle cx="12" cy="8" r="7" />
          <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
        </>
      );

    case 'cake':
      return (
        <>
          <path d="M20 21v-8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8" />
          <path d="M4 16s.5-1 2-1 2.5 2 4 2 2.5-2 4-2 2.5 2 4 2 2-1 2-1" />
          <line x1="2" y1="21" x2="22" y2="21" />
          <line x1="12" y1="8" x2="12" y2="4" />
          <path d="M12 4c.6 0 1-.4 1-1a1 1 0 0 0-2 0c0 .6.4 1 1 1z" />
        </>
      );

    case 'sparkles':
      return (
        <>
          <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z" />
        </>
      );

    // Analytics
    case 'trendingUp':
    case 'chartNoAxesCombined':
      return (
        <>
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
          <polyline points="17 6 23 6 23 12" />
        </>
      );

    case 'trendingDown':
      return (
        <>
          <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
          <polyline points="17 18 23 18 23 12" />
        </>
      );

    case 'barChart3':
      return (
        <>
          <line x1="12" y1="20" x2="12" y2="10" />
          <line x1="18" y1="20" x2="18" y2="4" />
          <line x1="6" y1="20" x2="6" y2="16" />
        </>
      );

    case 'lineChart':
      return (
        <>
          <path d="M3 3v18h18" />
          <path d="m19 9-5 5-4-4-3 3" />
        </>
      );

    case 'pieChart':
      return (
        <>
          <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
          <path d="M22 12A10 10 0 0 0 12 2v10z" />
        </>
      );

    case 'scale':
      return (
        <>
          <path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
          <path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
          <path d="M7 21h10" />
          <path d="M12 3v18" />
          <path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2" />
        </>
      );

    // Documents & Storage
    case 'files':
      return (
        <>
          <path d="M15.5 2H8.6c-.4 0-.8.2-1.1.5-.3.3-.5.7-.5 1.1v12.8c0 .4.2.8.5 1.1.3.3.7.5 1.1.5h9.8c.4 0 .8-.2 1.1-.5.3-.3.5-.7.5-1.1V6.5L15.5 2z" />
          <path d="M3 7.6v12.8c0 .4.2.8.5 1.1.3.3.7.5 1.1.5h9.8" />
          <polyline points="15 2 15 7 20 7" />
        </>
      );

    case 'file':
    case 'fileText':
      return (
        <>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <line x1="10" y1="9" x2="8" y2="9" />
        </>
      );

    case 'filePdf':
      return (
        <>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <path d="M9 13v4" />
          <path d="M9 13h2a1 1 0 0 1 1 1v0a1 1 0 0 1-1 1H9" />
          <path d="M14 13v4" />
        </>
      );

    case 'fileCheck':
      return (
        <>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <polyline points="9 15 11 17 15 13" />
        </>
      );

    case 'fileArchive':
      return (
        <>
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="10" y1="8" x2="14" y2="8" />
          <line x1="10" y1="12" x2="14" y2="12" />
          <line x1="10" y1="16" x2="14" y2="16" />
        </>
      );

    case 'fileSpreadsheet':
      return (
        <>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <path d="M8 13h8" />
          <path d="M8 17h8" />
          <path d="M12 13v8" />
        </>
      );

    case 'folder':
      return (
        <>
          <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z" />
        </>
      );

    case 'layers':
      return (
        <>
          <polygon points="12 2 2 7 12 12 22 7 12 2" />
          <polyline points="2 17 12 22 22 17" />
          <polyline points="2 12 12 17 22 12" />
        </>
      );

    case 'database':
      return (
        <>
          <ellipse cx="12" cy="5" rx="9" ry="3" />
          <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
          <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
        </>
      );

    case 'monitor':
      return (
        <>
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
        </>
      );

    case 'smartphone':
      return (
        <>
          <rect x="5" y="2" width="14" height="20" rx="2" />
          <line x1="12" y1="18" x2="12.01" y2="18" />
        </>
      );

    default:
      return <circle cx="12" cy="12" r="8" />;
  }
}
